import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
// Redux store
import { useSelector, useDispatch } from 'react-redux';
import { getVideoInfo } from '../../features/video/videoSlice';
// Reusable components
import { SharablePopup, TranscriptContainer, CustomVideo } from '../../components/videos';
import MainLoader from '../../components/Structures/MainLoader';
// HTTP client
import axios from 'axios';

const CreateClip = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { videoId, state } = useParams();

  // Clip start/end times and positions from URL
  const start = searchParams.get('start');
  const end = searchParams.get('end');
  const startPosition = searchParams.get('sp');
  const endPosition = searchParams.get('ep');

  const { video, isLoading, isError } = useSelector((s) => s.video);

  // transcript will always be nested: array of paragraphs (each paragraph is an array)
  const [transcript, setTranscript] = useState([]);
  const [inputs, setInputs] = useState({ 'include-transcript': true });
  const [showPopup, setShowPopup] = useState(false);
  const [isLoadingClip, setIsLoadingClip] = useState(false);
  const [clipID, setClipID] = useState();

  // 1) Load video info on mount or when videoId/state change
  useEffect(() => {
    if (videoId) {
      dispatch(getVideoInfo({ videoId, state }));
    }
  }, [videoId, state, dispatch]);

  // 2) When video.transcript arrives, extract the correct slice and normalize to nested format
  useEffect(() => {
    if (!video.transcript) return;

    // Detect flat vs. nested structure
    const isFlat =
      Array.isArray(video.transcript) && video.transcript.length > 0 && video.transcript[0].hasOwnProperty('text');

    // Parse our clip start/end (seconds) from query params
    const startSec = parseFloat(start);
    const endSec = parseFloat(end);

    if (isFlat) {
      // FLAT transcript: filter chunks by time overlap [startSec, endSec]
      const filtered = video.transcript.filter(
        (chunk) =>
          // include any chunk that overlaps our clip window
          chunk.endTime >= startSec && chunk.startTime <= endSec
      );

      // wrap each chunk into its own paragraph array
      const nested = filtered.map((chunk) => chunk);
      setTranscript(nested);
    } else {
      // NESTED transcript: slice paragraphs & words by index as before
      const [startPara, startWord] = startPosition.split('-').map((n) => parseInt(n, 10));
      const [endPara, endWord] = endPosition.split('-').map((n) => parseInt(n, 10));

      const sliceParas = video.transcript.slice(startPara, endPara + 1);
      const subTranscript = sliceParas.map((para, idx) => {
        if (sliceParas.length === 1) {
          return para.slice(startWord, endWord + 1);
        }
        if (idx === 0) {
          return para.slice(startWord);
        }
        if (idx === sliceParas.length - 1) {
          return para.slice(0, endWord + 1);
        }
        return para;
      });
      setTranscript(subTranscript);
    }
  }, [video.transcript, start, end, startPosition, endPosition]);

  // console.log(transcript);

  // 3) Handle form submission to create the clip
  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowPopup(true);
    setIsLoadingClip(true);
    try {
      const { data } = await axios.post(`/api/${state}/video/clip`, {
        VideoID: video.VideoId,
        Title: inputs.title || `${video.Committee} clip`,
        Description: inputs.description || null,
        StartTime: start,
        EndTime: end,
        StartPosition: startPosition,
        EndPosition: endPosition,
        IncludeTranscript: inputs['include-transcript'],
      });
      setClipID(data.id);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingClip(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };
  const handleChangeCheckbox = () => {
    setInputs((prev) => ({
      ...prev,
      'include-transcript': !prev['include-transcript'],
    }));
  };

  // Close popup and navigate back to video view
  const handleClose = useCallback(() => {
    setShowPopup(false);
    navigate(`/${state}/video/${videoId}`);
  }, [navigate, state, videoId]);

  if (isLoading) return <MainLoader />;
  if (isError) return <div>Error loading video</div>;

  return (
    <div className="w-full flex gap-5">
      {video && (
        <>
          {/* Left panel: clip creation form and preview */}
          <div className="w-[40%] h-full overflow-hidden">
            <div className="py-[15px] px-5 border border-[#E5E7EB] bg-[#F9FAFB] mb-4">
              <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
                <span className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8">
                  Create video clip: <strong>{inputs.title || video.Committee}</strong>
                </span>
              </div>
              <CustomVideo start={start} end={end} url={video.Link} />
              <form className="border-t border-gray-300 pb-4 flex flex-col gap-4 mt-3 pt-3" onSubmit={handleSubmit}>
                <div>
                  <label className="font-playfair text-[22px] font-medium mb-2">Title</label>
                  <input
                    className="w-full border border-[#E5E7EB] bg-white p-4 text-base"
                    name="title"
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="font-playfair text-[22px] font-medium mb-2">Description (optional)</label>
                  <textarea
                    className="w-full border border-[#E5E7EB] bg-white p-4 text-base max-h-[100px]"
                    name="description"
                    onChange={handleChange}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    id="include-transcript"
                    type="checkbox"
                    className="w-5 h-5 border border-[#E5E7EB]"
                    checked={inputs['include-transcript']}
                    onChange={handleChangeCheckbox}
                  />
                  <label htmlFor="include-transcript" className="text-sm">
                    Include transcript
                  </label>
                </div>
                <button type="submit" className="py-3 px-8 bg-[#83B2FA] text-white font-semibold hover:bg-darkblue">
                  CREATE CLIP
                </button>
              </form>
            </div>
          </div>

          {/* Right panel: nested transcript preview */}
          <TranscriptContainer transcript={transcript} miniTranscript />
        </>
      )}

      {/* Shareable popup after clip creation */}
      {showPopup && (
        <SharablePopup setPopup={setShowPopup} isLoading={isLoadingClip} uuid={clipID} handleClose={handleClose} />
      )}
    </div>
  );
};

export default CreateClip;
